import { useRouter } from 'next/router'
import { useCallback, useEffect } from 'react'
import { useMixpanel } from '@captaincause/lib/mixpanel'
import { tm } from '@captaincause/ui/tm'
import { fonts } from 'fonts'
import { BaseLayout } from './Base'
import type {
  Campaign,
  Dift,
  DirectCampaign,
  Project,
} from '@captaincause/types'

interface AppLayoutProps {
  children: JSX.Element
  Component: ((props: any) => JSX.Element) | null
  pageProps?: any
}

type PageProps = Record<string, any> & {
  dift?: Dift
  campaign?: Campaign
  projects?: Project[]
  directCampaign?: DirectCampaign
  origin?: string
}

export interface LayoutProps {
  children: JSX.Element
  className?: string
  pageProps?: PageProps
}

function getMixpanelPayload(pageProps: PageProps) {
  let payload = {}

  if (pageProps?.dift) {
    payload = {
      dift_amount: pageProps.dift?.amount?.amount,
      dift_type: pageProps.dift?.type,
      dift_project_count: pageProps.dift?.projects?.length,
    }
  }
  if (pageProps?.origin) {
    payload = {
      ...payload,
      widget_campaign_token: pageProps?.token,
      widget_type: pageProps?.type,
      widget_origin: pageProps.origin,
    }
  }
  if (pageProps?.campaign) {
    payload = {
      ...payload,
      campaign_name: pageProps.campaign?.name,
      campaign_token: pageProps.campaign?.live_token,
      campaign_amount: pageProps.campaign?.displayed_amount?.amount,
      ask_for_code: pageProps.campaign?.ask_for_code,
      ask_for_email: pageProps.campaign?.ask_for_email,
      can_bypass_email: pageProps.campaign?.can_bypass_email,
      enable_widget: pageProps.campaign?.enable_widget,
      hide_micro_impact: pageProps.campaign?.hide_micro_impact,
      is_consult: pageProps.campaign?.is_consult,
      is_done: pageProps.campaign?.is_done,
      is_individual_dift: pageProps.campaign?.is_individual_dift,
      is_preview: pageProps.campaign?.is_preview,
      is_restricted: pageProps.campaign?.is_restricted,
      is_saturated: pageProps.campaign?.is_saturated,
      is_test: pageProps.campaign?.is_test,
      points_mode: pageProps.campaign?.points_mode,
      points_value_in_cents: pageProps.campaign?.points_value_in_cents,
      campaign_project_count: pageProps.projects?.length,
      enable_progress_bar: pageProps.campaign?.enable_progress_bar,
    }
  }
  if (pageProps?.directCampaign) {
    payload = {
      ...payload,
      direct_campaign_id: pageProps.directCampaign?.id,
      direct_campaign_name: pageProps.directCampaign?.name,
      direct_campaign_project_count: pageProps.directCampaign?.projects?.length,
    }
  }

  return payload
}

export function AppLayout({
  Component,
  children,
  pageProps = undefined,
}: AppLayoutProps) {
  const { track, register } = useMixpanel()
  const router = useRouter()

  const mixpanelPayload = getMixpanelPayload(pageProps)

  const trackPage = useCallback(() => {
    track('View page', {
      pathname: router.pathname,
      qToken: router.query.token,
      ...mixpanelPayload,
    })
  }, [track, router.pathname, router.query.token, mixpanelPayload])

  useEffect(() => {
    register({ app: 'web' })
  }, [register])

  useEffect(() => {
    trackPage()
  }, [trackPage])

  if (Component) {
    return (
      <Component
        className={tm('grid min-h-screen', fonts)}
        pageProps={pageProps}
      >
        {children}
      </Component>
    )
  }

  return (
    <BaseLayout
      className={tm('grid min-h-screen', fonts)}
      pageProps={pageProps}
    >
      {children}
    </BaseLayout>
  )
}
